import {action, observable, runInAction} from "mobx";
import optionService from "../services/optionService";
import {loadStore, autoSave} from "../_core/mobxLocalStorage";
import {toTurkishProperCase} from "utils/toTurkishProperCase";

const CACHE_EXPIRY_MS = 10 * 60 * 1000; // 10 dakika

class OptionStore {
    constructor() {
        loadStore(this, "optionStore");
        autoSave(this, "optionStore");
    }

    @observable cities = [];
    @observable lastCityUpdate = null;

    @observable districtsCache = {};
    @observable lastDistrictUpdate = null;

    @observable neighborhoodsCache = {};
    @observable lastNeighborhoodUpdate = null;

    @action resetOptionsStoreData = () => {
        this.cities = [];
        this.lastCityUpdate = null;
        this.districtsCache = {};
        this.lastDistrictUpdate = null;
        this.neighborhoodsCache = {};
        this.lastNeighborhoodUpdate = null;
    };
    // City Actions
    @action loadCities = async (params = {}) => {
        const isCacheValid =
            this.lastCityUpdate &&
            Date.now() - this.lastCityUpdate < CACHE_EXPIRY_MS &&
            this.cities.length > 0;

        if (isCacheValid) {
            return this.cities;
        }

        try {
            const response = await optionService.getCityList(params);
            if (response.status !== 200) {
                console.error("City list fetch failed:", response.data.message);
                return [];
            }

            const cities =
                response?.data?.data.map(cityData => ({
                    ...cityData,
                    name: toTurkishProperCase(cityData?.name) || cityData?.name || ""
                })) || [];

            runInAction(() => {
                this.cities = cities;
                this.lastCityUpdate = Date.now();
            });

            return cities;
        } catch (error) {
            console.error("City list fetch error:", error);
            return [];
        }
    };

    @action getCityList = async (params = {}) => {
        try {
            const response = await optionService.getCityList(params);
            if (response.status !== 200) {
                console.error("City list fetch failed:", response.data.message);
                return {data: []};
            }
            return response.data;
        } catch (error) {
            console.error("City list fetch error:", error);
            return {data: []};
        }
    };

    @action getCityById = async params => {
        try {
            const response = await optionService.getCityById(params);
            if (response.status !== 200) {
                console.error("City fetch failed:", response.data.message);
                return false;
            }
            return response.data;
        } catch (error) {
            console.error("City fetch error:", error);
            return false;
        }
    };

    // District Actions
    @action loadDistricts = async (params = {}) => {
        // cityName veya cityId'den en az biri olmalı
        if ((!params.cityName || params.cityName === "all") && !params.cityId) {
            return [];
        }

        const cacheKey = this._getDistrictCacheKey(params);
        if (!cacheKey) {
            return [];
        }

        // Cache geçerliyse direkt dön
        const isCacheValid =
            this.lastDistrictUpdate &&
            Date.now() - this.lastDistrictUpdate < CACHE_EXPIRY_MS &&
            this.districtsCache?.[cacheKey];

        if (isCacheValid) {
            return this.districtsCache[cacheKey];
        }

        try {
            const response = await optionService.getDistrictList(params);
            if (response.status !== 200) {
                console.error("District list fetch failed:", response.data.message);
                return [];
            }

            const districts =
                response.data?.data.map(districtData => ({
                    ...districtData,
                    name: toTurkishProperCase(districtData?.name) || districtData?.name || ""
                })) || [];

            runInAction(() => {
                this.districtsCache[cacheKey] = districts;
                this.lastDistrictUpdate = Date.now();
            });

            return districts;
        } catch (error) {
            console.error("District list fetch error:", error);
            return [];
        }
    };

    // Cache key oluşturma helper fonksiyonu
    _getDistrictCacheKey = params => {
        if (params.cityName && params.cityName !== "all") {
            return `cityName:${params.cityName.toLocaleLowerCase("tr-TR")}`;
        }
        if (params.cityId) {
            return `cityId:${params.cityId}`;
        }
        return null;
    };

    @action getDistrictList = async (params = {}) => {
        try {
            const response = await optionService.getDistrictList(params);
            if (response.status !== 200) {
                console.error("District list fetch failed:", response.data.message);
                return {data: []};
            }
            return response.data;
        } catch (error) {
            console.error("District list fetch error:", error);
            return {data: []};
        }
    };

    @action getDistrictById = async params => {
        try {
            const response = await optionService.getDistrictById(params);
            if (response.status !== 200) {
                console.error("District fetch failed:", response.data.message);
                return false;
            }
            return response.data;
        } catch (error) {
            console.error("District fetch error:", error);
            return false;
        }
    };

    // Neighborhood Actions
    @action loadNeighborhoods = async (params = {}) => {
        // districtId olmalı
        if (!params.districtId) {
            return [];
        }

        const cacheKey = this._getNeighborhoodCacheKey(params);
        if (!cacheKey) {
            return [];
        }

        // Cache geçerliyse direkt dön
        const isCacheValid =
            this.lastNeighborhoodUpdate &&
            Date.now() - this.lastNeighborhoodUpdate < CACHE_EXPIRY_MS &&
            this.neighborhoodsCache?.[cacheKey];

        if (isCacheValid) {
            return this.neighborhoodsCache[cacheKey];
        }

        try {
            const response = await optionService.getNeighborhoodList(params);
            if (response.status !== 200) {
                console.error("Neighborhood list fetch failed:", response.data.message);
                return [];
            }

            const neighborhoods =
                response.data?.data.map(neighborhoodData => ({
                    ...neighborhoodData,
                    name:
                        toTurkishProperCase(neighborhoodData?.name) || neighborhoodData?.name || ""
                })) || [];

            runInAction(() => {
                this.neighborhoodsCache[cacheKey] = neighborhoods;
                this.lastNeighborhoodUpdate = Date.now();
            });

            return neighborhoods;
        } catch (error) {
            console.error("Neighborhood list fetch error:", error);
            return [];
        }
    };

    // Cache key oluşturma helper fonksiyonu
    _getNeighborhoodCacheKey = params => {
        if (params.districtId) {
            return `districtId:${params.districtId}`;
        }
        return null;
    };

    @action getNeighborhoodList = async (params = {}) => {
        try {
            const response = await optionService.getNeighborhoodList(params);
            if (response.status !== 200) {
                console.error("Neighborhood list fetch failed:", response.data.message);
                return {data: []};
            }
            return response.data;
        } catch (error) {
            console.error("Neighborhood list fetch error:", error);
            return {data: []};
        }
    };

    @action getNeighborhoodById = async params => {
        try {
            const response = await optionService.getNeighborhoodById(params);
            if (response.status !== 200) {
                console.error("Neighborhood fetch failed:", response.data.message);
                return false;
            }
            return response.data;
        } catch (error) {
            console.error("Neighborhood fetch error:", error);
            return false;
        }
    };
}

export default new OptionStore();
